import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/shared/ui/theme/ThemeProvider';
import { CountdownBar } from '@/presentation/components/CountdownBar';
import { AuthMethodSwitcher } from '@/presentation/components/AuthMethodSwitcher';
import { useClock } from '@/shared/hooks/useClock';
import { formatTimeFull } from '@/shared/utils/datetime';
import { KIOSK_AUTO_RESET_SECONDS } from '@/shared/config/terminal.config';
import type { AttendanceAction } from '../../domain/entities/Timelog';

const ADMIN_RESET_SECONDS = 5;

/**
 * Maps a terminal error code to an i18n key, branching on action (enroll vs auth).
 * Enroll codes map to enrollFaceError.* namespace; auth codes map to faceError.*.
 */
function resolveErrorMessageKey(params: {
  action: AttendanceAction | 'enroll';
  errorCode?: string;
  subreason?: 'blur' | 'brightness' | 'size';
}): string {
  const { action, errorCode, subreason } = params;
  const isEnroll = action === 'enroll';

  if (isEnroll) {
    // Enroll-specific namespace
    if (errorCode === 'NO_FACE')           return 'enrollFaceError.noFace';
    if (errorCode === 'MULTIPLE_FACES')    return 'enrollFaceError.multipleFaces';
    if (errorCode === 'LOW_QUALITY') {
      if (subreason === 'blur')       return 'enrollFaceError.lowQualityBlur';
      if (subreason === 'brightness') return 'enrollFaceError.lowQualityBrightness';
      if (subreason === 'size')       return 'enrollFaceError.lowQualitySize';
      return 'enrollFaceError.lowQuality';
    }
    if (errorCode === 'LIVENESS_SPOOF')        return 'enrollFaceError.livenessSpoof';
    if (errorCode === 'LIVENESS_INCONCLUSIVE') return 'enrollFaceError.livenessInconclusive';
    if (errorCode === 'EMBEDDING_TIMEOUT')     return 'enrollFaceError.embeddingTimeout';
    if (errorCode === 'EMBEDDING_FAILED')      return 'enrollFaceError.embeddingFailed';
    if (errorCode === 'CAMERA_DENIED')         return 'enrollFaceError.cameraDenied';
    if (errorCode === 'MODEL_LOAD')            return 'enrollFaceError.modelLoad';
    if (errorCode === 'CAMERA_ERROR')          return 'enrollFaceError.cameraError';
    if (errorCode === 'DUPLICATE_FACE')        return 'faceError.duplicateFace'; // Shared key
    return 'enrollFaceError.generic';
  }

  // Auth flow — codes previously surfaced only by enroll are now emitted by
  // useScanLifecycle after the liveness-aggregator unification. Map each to its
  // auth-namespaced copy so the operator sees the specific cause instead of the
  // generic "could not identify" fallback.
  if (errorCode === 'NO_MATCH')             return 'faceError.noMatch';
  if (errorCode === 'SPOOF')                return 'faceError.spoof';
  if (errorCode === 'LOW_QUALITY') {
    // Mirrors the enroll subreason branch so blur / brightness / size render
    // the actionable copy instead of the generic fallback.
    if (subreason === 'blur')       return 'faceError.lowQualityBlur';
    if (subreason === 'brightness') return 'faceError.lowQualityBrightness';
    if (subreason === 'size')       return 'faceError.lowQualitySize';
    return 'faceError.lowQuality';
  }
  if (errorCode === 'LIVENESS_SPOOF')       return 'faceError.livenessSpoof';
  if (errorCode === 'LIVENESS_INCONCLUSIVE') return 'faceError.livenessInconclusive';
  if (errorCode === 'LIVENESS_BORDERLINE')  return 'faceError.livenessBorderline';
  if (errorCode === 'NO_FACE')              return 'faceError.noFace';
  if (errorCode === 'MULTIPLE_FACES')       return 'faceError.multipleFaces';
  if (errorCode === 'CAMERA_DENIED')        return 'faceError.cameraDenied';
  if (errorCode === 'MODEL_LOAD')           return 'faceError.modelLoad';
  if (errorCode === 'CAMERA_ERROR')         return 'faceError.cameraError';
  if (errorCode === 'DUPLICATE_FACE')       return 'faceError.duplicateFace';
  return 'faceError.hint';
}

export function FaceAuthErrorPage() {
  const { t } = useTranslation('common');
  const { C, T } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  // `enroll` is not a runtime AttendanceAction — it routes back to the admin flow.
  const state = location.state as {
    action?: AttendanceAction | 'enroll';
    errorCode?: string;
    idPerson?: string;
    idEntity?: number;
    subreason?: 'blur' | 'brightness' | 'size';
  } | null;
  const action = state?.action ?? 'entrada';
  const errorCode = state?.errorCode;
  // idPerson kept in state interface for backward compat but no longer used (replaced by idEntity)
  const idEntity = state?.idEntity;
  const subreason = state?.subreason;

  const now = useClock();
  const isEnroll = action === 'enroll';
  const isAdmin = action === 'acceso-admin' || isEnroll;   // enroll is part of admin context
  const isEmployee = action === 'acceso-empleado';
  const isAttendance = !isAdmin && !isEmployee && !isEnroll;

  const resetSec = isAdmin ? ADMIN_RESET_SECONDS : KIOSK_AUTO_RESET_SECONDS;
  const messageKey = resolveErrorMessageKey({
    action,
    ...(errorCode !== undefined && { errorCode }),
    ...(subreason !== undefined && { subreason }),
  });

  // For enroll errors, route back to the employee detail page via idEntity (the DB PK).
  // If idEntity is missing, fall back to the employee list page (prevents /undefined routes).
  const recoveryTarget = isEnroll
    ? (idEntity != null ? `/admin/empleados/${idEntity}` : '/admin/empleados')
    : '/';
  const retryTarget = isEnroll
    ? (idEntity != null ? `/admin/empleados/${idEntity}` : '/admin/empleados')
    : `/scan/${action}`;

  useEffect(() => {
    const timer = setTimeout(() => { void navigate(recoveryTarget, { replace: true }); }, resetSec * 1000);
    return () => { clearTimeout(timer); };
  }, [navigate, resetSec, recoveryTarget]);

  return (
    <div style={{ flex: 1, background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '30px' }}>
      <div className="mi-anim-slide-up" style={{ flex: 1, width: '100%', maxWidth: 407, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {/* Danger icon */}
        <div className="mi-anim-check-pop" style={{ marginBottom: 23 }}>
          <div style={{ width: 99, height: 99, borderRadius: '50%', background: `${C.danger}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 75, height: 75, borderRadius: '50%', background: C.danger, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width={33} height={33} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          </div>
        </div>

        <div style={{ fontSize: 23, fontWeight: 700, color: T.clockText, marginBottom: 7 }}>
          {isEnroll ? t('enrollFaceError.title') : t('faceError.title')}
        </div>
        <p style={{ fontSize: 12, fontWeight: 700, color: T.text, textAlign: 'center', lineHeight: 1.7, marginBottom: 7, maxWidth: 330 }}>
          {t(messageKey)}
        </p>
        <div style={{ fontSize: 11, fontWeight: 700, color: T.text, marginBottom: 26 }}>{formatTimeFull(now)}</div>

        {/* Countdown */}
        <div style={{ width: '100%', marginBottom: 23 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 500 }}>{t('faceError.countdown')}</span>
            <span style={{ fontSize: 10, color: T.textMuted }}>{resetSec}s</span>
          </div>
          <CountdownBar seconds={resetSec} color={C.danger} T={T} />
        </div>

        {/* Primary action: Retry face */}
        <button
          onClick={() => { void navigate(retryTarget, { replace: true }); }}
          style={{ padding: '11px 28px', borderRadius: 9, border: 'none', background: C.primary, color: '#fff', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 16 }}
        >
          <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M3 12a9 9 0 1 0 3-6.7"/><path d="M3 4v5h5"/></svg>
          {t('faceError.retryFace')}
        </button>

        {/* Fallback method buttons (enroll context has no fallback — it routes back to admin) */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
          {/* PIN — shown for admin/employee terminal-flow actions only (attendance uses AuthMethodSwitcher) */}
          {!isEnroll && !isAttendance && (isAdmin || isEmployee) && (
            <button
              onClick={() => { void navigate(`/id-input/${action}`, { replace: true }); }}
              style={{ padding: '9px 20px', borderRadius: 9, border: `1px solid ${C.blue}40`, background: `${C.blue}12`, color: C.blue, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
              {t('faceError.usePin')}
            </button>
          )}
          {/* Card — only for terminal admin access, not for enroll */}
          {isAdmin && !isEnroll && (
            <button
              onClick={() => { void navigate('/card-scan/acceso-admin', { replace: true }); }}
              style={{ padding: '9px 20px', borderRadius: 9, border: `1px solid ${C.blue}40`, background: `${C.blue}12`, color: C.blue, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="13" rx="2"/><path d="M16 12h.01M13 12h.01"/></svg>
              {t('faceError.useCard')}
            </button>
          )}
          {/* AuthMethodSwitcher: attendance flow — unconditional 2-method switcher (spec: "always show 2 alternate methods") */}
          {isAttendance && (
            <AuthMethodSwitcher
              currentMethod="facial"
              action={action as AttendanceAction}
              accent={C.blue}
              t={t}
              replace
            />
          )}
        </div>

        <button
          onClick={() => { void navigate(recoveryTarget, { replace: true }); }}
          style={{ padding: '8px 22px', borderRadius: 9, border: `1px solid ${T.border}`, background: 'transparent', fontFamily: 'inherit', fontSize: 10, fontWeight: 600, color: T.textMuted, cursor: 'pointer' }}
        >
          {t('faceError.returnMenu')}
        </button>
      </div>
    </div>
  );
}
